import React from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { MapPin, Clock, ChevronRight } from 'lucide-react';

const STATUS_BADGE = {
  open:      'bg-sky-100 text-sky-700',
  expired:   'bg-gray-100 text-gray-500',
  cancelled: 'bg-red-100 text-red-600',
  completed: 'bg-emerald-100 text-emerald-700',
  paid:      'bg-teal-100 text-teal-700',
  disputed:  'bg-orange-100 text-orange-700',
  resolved:  'bg-purple-100 text-purple-700',
};

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function CoverageBar({ pct = 0 }) {
  const p     = Math.min(Math.max(pct, 0), 100);
  const color = p === 100 ? 'bg-emerald-500' : p >= 50 ? 'bg-amber-400' : 'bg-red-400';
  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 w-20 overflow-hidden rounded-full bg-gray-100">
        <div className={`h-full rounded-full transition-all ${color}`} style={{ width: `${p}%` }} />
      </div>
      <span className="tabular-nums text-xs text-gray-500">{Math.round(p)}%</span>
    </div>
  );
}

/**
 * ListView — clean table of work orders.
 * Entire row is clickable — no separate "Details" button.
 */
const ListView = ({ data, getDeadlineText }) => {
  const { customerCompanyId } = useParams();
  const history = useHistory();

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      {/* Header */}
      <div className="grid grid-cols-[2fr_1fr_1fr_1fr_1fr_32px] gap-4 border-b border-gray-100 bg-gray-50 px-5 py-2.5">
        {['Location', 'Service', 'Next shift', 'Coverage', 'Status', ''].map(h => (
          <p key={h} className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">{h}</p>
        ))}
      </div>

      {/* Rows */}
      <div className="divide-y divide-gray-100">
        {data.map((job) => {
          const pct         = job?.statistics?.overallCoverage ?? 0;
          const statusKey   = job?.status?.toLowerCase() || '';
          const badgeCls    = STATUS_BADGE[statusKey] || 'bg-gray-100 text-gray-500';
          const deadlineTxt = getDeadlineText(job?.shifts);
          const nextShift   = job?.shifts?.length
            ? job.shifts.reduce((a, b) => new Date(a.startTime) < new Date(b.startTime) ? a : b)
            : null;

          return (
            <div
              key={job._id}
              onClick={() => history.push(`/customer-company/${customerCompanyId}/jobs/${job._id}`)}
              className="grid cursor-pointer grid-cols-[2fr_1fr_1fr_1fr_1fr_32px] items-center gap-4 px-5 py-3.5 transition-colors hover:bg-gray-50"
            >
              {/* Location */}
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-gray-900">
                  {job.location || job.formattedAddress || '—'}
                </p>
                <p className="mt-0.5 flex items-center gap-1 truncate text-xs text-gray-400">
                  <MapPin size={10} className="flex-shrink-0" />
                  {job.formattedAddress || job.city || '—'}
                </p>
              </div>

              {/* Service */}
              <p className="truncate text-sm text-gray-700">{job.serviceType || '—'}</p>

              {/* Next shift */}
              <div>
                {nextShift ? (
                  <>
                    <p className="text-xs font-medium text-gray-900">{fmtDate(nextShift.date || nextShift.startTime)}</p>
                    <p className="mt-0.5 flex items-center gap-1 text-[11px] text-gray-400">
                      <Clock size={9} />
                      {deadlineTxt}
                    </p>
                  </>
                ) : (
                  <p className="text-xs text-gray-400">No shifts</p>
                )}
              </div>

              {/* Coverage */}
              <CoverageBar pct={pct} />

              {/* Status */}
              <span className={`inline-flex w-fit rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${badgeCls}`}>
                {job.status ? job.status.charAt(0).toUpperCase() + job.status.slice(1) : '—'}
              </span>

              {/* Chevron */}
              <ChevronRight size={14} className="text-gray-300" />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ListView;
